import { useTranslation } from 'react-i18next';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextStdMd } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { color } from '@/colors';
import { HandleType } from '@/enums/handle-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { useObscuredNodeFilter } from './useObscuredNodeFilter';
import { COLLAPSE_TRANSITION_MS, HANDLE_SPACING_PX, handleTypeLabelKey } from './handleCollapse';
import { getHandleColor } from './HandlesUtils';

interface CollapsedGroupHandleProps {
  type: HandleType;
  memberCount: number;
  index: number;
  handleTop: string;
  isExpanded: boolean;
  hasError: boolean;
  /** Whether an in-progress connection drag makes this group an invalid drop target. */
  isObscured: boolean;
  showLabel?: boolean;
  onToggle: () => void;
}

/** A plain button, not a React Flow `Handle`: it stands for N inputs and has no id to connect to. */
export const CollapsedGroupHandle = ({
  type,
  memberCount,
  index,
  handleTop,
  isExpanded,
  hasError,
  isObscured,
  showLabel = false,
  onToggle,
}: CollapsedGroupHandleProps) => {
  const { t } = useTranslation();
  const obscuredFilter = useObscuredNodeFilter();
  const role = useUserWorkflowRole();
  const typeColor = getHandleColor(type);
  const label = `${memberCount} ${t(handleTypeLabelKey(type), { count: memberCount })}`;

  return (
    <Tooltip
      title={t(
        isExpanded
          ? I18N_KEYS.RECIPE_MAIN.NODES.NODE_HANDLES.GROUP_CLOSE_TOOLTIP
          : I18N_KEYS.RECIPE_MAIN.NODES.NODE_HANDLES.GROUP_OPEN_TOOLTIP,
      )}
      placement="top"
    >
      <Box
        component="button"
        type="button"
        aria-expanded={isExpanded}
        aria-label={label}
        data-testid={`handle-group-${type}`}
        className="nodrag"
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          // `pointerEvents: 'none'` below cannot carry this on its own: this is a real `<button>`,
          // and a keyboard activation fires `click` without consulting pointer-events. Without this
          // line a guest could tab here and press Enter, reaching a persisted `graph.edit` write.
          if (role === 'guest') return;
          onToggle();
        }}
        sx={{
          position: 'absolute',
          top: `calc(${handleTop} + ${index * HANDLE_SPACING_PX}px)`,
          // React Flow centres a handle on its `top` via a translate; match it or every group row
          // sits half a handle lower than the plain rows beside it.
          transform: 'translateY(-50%)',
          left: -16,
          width: 32,
          height: 32,
          padding: 0,
          borderRadius: '50%',
          border: '8px solid',
          borderColor: hasError ? color.Weavy_Error : cssVars.color.bg,
          backgroundColor: typeColor,
          cursor: role === 'guest' ? 'default' : 'pointer',
          // Same gate DynamicHandleLabel applies to every plain handle: a guest can look but not
          // reach `flowView.setHandleCollapse`, which persists and is undoable.
          pointerEvents: role === 'guest' ? 'none' : 'auto',
          filter: isObscured ? obscuredFilter : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: ZIndex.Flow.NODE_HANDLE,
          transition: `top ${COLLAPSE_TRANSITION_MS}ms ease-in-out`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            color: cssVars.color.bg,
            transform: isExpanded ? 'none' : 'rotate(180deg)',
            transition: `transform ${COLLAPSE_TRANSITION_MS}ms ease-in-out`,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <g transform="translate(4.35 5.45)">
              <path
                d="M1.70605 4.35791C1.31553 4.74843 0.682517 4.74843 0.291992 4.35791C-0.0983128 3.96737 -0.0984591 3.3343 0.291992 2.94385L2.94336 0.29248C3.33381 -0.0979112 3.9669 -0.097801 4.35742 0.29248L7.00977 2.94385C7.4 3.3343 7.39996 3.96743 7.00977 4.35791C6.61931 4.74837 5.98624 4.74823 5.5957 4.35791L3.65039 2.41357L1.70605 4.35791Z"
                fill="currentColor"
              />
            </g>
          </svg>
        </Box>
        <TextStdMd
          component="span"
          sx={{
            position: 'absolute',
            // Anchored by its right edge, so a long group name grows away from the node instead of
            // over it, and needs no measurement to be correct on the first frame. The offset
            // resolves against the button's padding box, which sits inside its own 8px border.
            right: 30,
            top: -20,
            width: 'fit-content',
            whiteSpace: 'nowrap',
            // DynamicHandleLabel capitalises, which would render "7 Texts".
            textTransform: 'none',
            fontFamily: 'DM Mono',
            color: hasError ? token.textDanger : typeColor,
            opacity: showLabel ? 1 : 0,
            pointerEvents: 'none',
          }}
        >
          {label}
        </TextStdMd>
      </Box>
    </Tooltip>
  );
};
